// The signed-in user's own account settings (see `features/user-settings.md`),
// opened from the account menu in the app bar. Deliberately a dialog rather than
// a screen: the same component then serves all three surfaces — the desktop
// admin layout, the mobile admin surface and the athlete home — without any of
// them needing a route or a tab of its own.
//
// Everything here is self-scoped: the server decides whose account this is from
// the session, so an athlete or guardian login (no permissions, no companies)
// reaches it exactly like a Super Admin does. Grants, company links and the
// blocked flag are *not* editable here — those stay in the Users section behind
// EDIT_USER. Email is shown but not editable: it is the identity a login link is
// sent to, so changing it needs a possession proof this dialog cannot give.

import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { PASSWORD_MIN, USER_NAME_MAX, type LanguageCode } from '../domain';
import { changeOwnPassword, updateOwnProfile } from '../data/apiClient';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from '../state/I18nContext';
import { LOCALES, LOCALE_META, toLocale } from '../i18n/messages';

interface AccountSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

export function AccountSettingsDialog({ open, onClose }: AccountSettingsDialogProps) {
  const { me, refreshMe } = useAuth();
  const { t, setLocale } = useTranslation();

  const [name, setName] = useState('');
  // '' means "follow my club's language" (stored as null server-side).
  const [language, setLanguage] = useState<LanguageCode | ''>('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Re-seed the draft from the live account each time the dialog opens, so a
  // cancelled edit never lingers into the next visit.
  useEffect(() => {
    if (!open) return;
    setName(me?.user.name ?? '');
    setLanguage(me?.user.language ?? '');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setSaved(false);
  }, [open, me]);

  const changingPassword =
    currentPassword.length > 0 || newPassword.length > 0 || confirmPassword.length > 0;

  const save = async () => {
    if (!me) return;
    if (changingPassword) {
      if (newPassword.length < PASSWORD_MIN) {
        setError(t('account.errors.passwordShort', { min: PASSWORD_MIN }));
        return;
      }
      if (newPassword !== confirmPassword) {
        setError(t('account.errors.passwordMismatch'));
        return;
      }
    }
    setBusy(true);
    setError(null);
    try {
      // Profile first: if the password change then fails (a mistyped current
      // password is the common case), the name / language edit is already safe
      // and only the password half needs retrying.
      await updateOwnProfile({ name, language: language === '' ? null : language });
      if (changingPassword) {
        await changeOwnPassword({ currentPassword, newPassword });
      }
      // Apply the chosen language to this session too, so the UI switches with
      // the account rather than only on the next device. Clearing it drops the
      // explicit choice and falls back to the club default (see `I18nContext`).
      setLocale(language === '' ? null : toLocale(language));
      await refreshMe();
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSaved(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('account.errors.save'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('account.settings.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {saved && !error && (
            <Alert severity="success">{t('account.settings.saved')}</Alert>
          )}

          <TextField
            label={t('account.settings.emailLabel')}
            value={me?.user.email ?? ''}
            fullWidth
            disabled
            helperText={t('account.settings.emailHelp')}
          />

          <TextField
            label={t('account.settings.nameLabel')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            slotProps={{ htmlInput: { maxLength: USER_NAME_MAX } }}
          />

          <FormControl fullWidth>
            <InputLabel id="account-language-label" shrink>
              {t('account.settings.languageLabel')}
            </InputLabel>
            {/* `displayEmpty` so "follow the company default" reads as the chosen
                option rather than as an empty field. */}
            <Select
              labelId="account-language-label"
              label={t('account.settings.languageLabel')}
              value={language}
              displayEmpty
              notched
              onChange={(e) => setLanguage(e.target.value as LanguageCode | '')}
            >
              <MenuItem value="">{t('account.settings.languageDefault')}</MenuItem>
              {LOCALES.map((code) => (
                <MenuItem key={code} value={code}>
                  {LOCALE_META[code].label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle2">
              {t('account.settings.passwordSection')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('account.settings.passwordHelp')}
            </Typography>
          </Stack>

          <TextField
            label={t('account.settings.currentPasswordLabel')}
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('account.settings.newPasswordLabel')}
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            helperText={t('account.settings.passwordMinHelp', { min: PASSWORD_MIN })}
          />
          <TextField
            label={t('account.settings.confirmPasswordLabel')}
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('admin.common.close')}
        </Button>
        <Button variant="contained" onClick={() => void save()} disabled={busy || !me}>
          {t('admin.common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
